import { type ReactNode, useMemo } from 'react'

import type { ChatProvider as ChatService } from '@molecule/app-ai-chat'
import { getProvider as getChatProvider } from '@molecule/app-ai-chat'
import { getProvider as getEditorProvider } from '@molecule/app-code-editor'
import { createProvider as createEditor } from '@molecule/app-code-editor-monaco'
import type { HttpClient } from '@molecule/app-http'
import { getProvider as getWorkspaceProvider } from '@molecule/app-ide'
import { createProvider as createWorkspace } from '@molecule/app-ide-default'
import { getProvider as getPreviewProvider } from '@molecule/app-live-preview'
import { createProvider as createPreview } from '@molecule/app-live-preview-iframe'
import {
  ChatProvider,
  EditorProvider,
  HttpProvider,
  PreviewProvider,
  WorkspaceProvider,
} from '@molecule/app-react'

import { setupIdeProviders } from '../bonds/ide.js'
import type { PublicWorkspaceMode } from '../lib/public-workspace.js'
import { Workspace } from './Workspace.js'

// Wire the IDE-only bonds when this lazy chunk evaluates. Because this module
// is reached only through App's dynamic import of the Workspace route, the
// Monaco/chat/preview providers are code-split out of the landing entry.
setupIdeProviders()

// Enroll the user into the PWA the moment they enter the IDE — i.e. once they've
// submitted a prompt and opened the app. The marketing landing stays service-
// worker-free (no staleness for casual visitors); only engaged users get the
// installable/caching PWA. Dormant on the pre-launch static build: VITE_PWA is
// unset there, so this condition is false and ../pwa.js is never loaded (and the
// build emits a self-destroying /sw.js instead). Activates by building with
// VITE_PWA=true (see the deploy:prod:pwa script), which only matters once a
// prompt submit actually opens the IDE.
if (import.meta.env.VITE_PWA === 'true') {
  void import('../pwa.js').then((m) => m.registerPWA())
}

/**
 * Route boundary for the IDE workspace.
 *
 * Provides the editor/chat/workspace/preview contexts here — scoped to the
 * Workspace subtree — rather than at the app root, so their heavy providers
 * ship only with this lazy chunk. Mirrors the conditional nesting that
 * `MoleculeProvider` does for these same contexts.
 *
 * @returns The Workspace wrapped in the IDE service contexts.
 */
export function WorkspaceRoute({
  publicWorkspace,
  publicAccess,
}: {
  publicWorkspace?: PublicWorkspaceMode
  publicAccess?: { http: HttpClient; chat: ChatService }
} = {}) {
  // The ordinary route and public route render ONE Workspace. Separate provider
  // instances keep private editor tabs, preview URLs and layout state out of a share.
  const isolated = useMemo(
    () =>
      publicWorkspace
        ? { editor: createEditor(), preview: createPreview(), workspace: createWorkspace() }
        : null,
    [publicWorkspace?.slug],
  )
  const chat = publicAccess?.chat ?? getChatProvider()
  const workspace = isolated?.workspace ?? getWorkspaceProvider()
  const editor = isolated?.editor ?? getEditorProvider()
  const preview = isolated?.preview ?? getPreviewProvider()

  let tree: ReactNode = <Workspace publicWorkspace={publicWorkspace} />
  if (preview) tree = <PreviewProvider provider={preview}>{tree}</PreviewProvider>
  if (editor) tree = <EditorProvider provider={editor}>{tree}</EditorProvider>
  if (workspace) tree = <WorkspaceProvider provider={workspace}>{tree}</WorkspaceProvider>
  if (chat) tree = <ChatProvider provider={chat}>{tree}</ChatProvider>
  if (publicAccess) tree = <HttpProvider client={publicAccess.http}>{tree}</HttpProvider>
  return <>{tree}</>
}
